/**
 * Catalogue des étapes du moteur d'import ADM.
 *
 * Les fichiers sont traités CÔTÉ ADM et les données poussées via les API batch
 * V2 (`ImportEngine` backend) — plus d'envoi de fichiers à la V2, plus de
 * steps d'initialisation (l'école cible est déjà déployée). À garder
 * synchronisé avec `TemplateCatalog` et `ImportEngine::STEP_ORDER` backend.
 */

import type {
  ConflictPolicyValue,
  CsvDelimiter,
  DeploymentType,
  EmailMode,
  Encoding,
  NameMode,
  ParentToStudentStrategy,
  PhoneMode,
  StudentToClassStrategy,
  TeacherToSubjectStrategy,
} from './types';

export type StepCategory = 'init' | 'import' | 'assignment' | 'post';

export interface LogicalFileSpec {
  /** Nom logique = nom du champ de formulaire à l'upload (ex: "students"). */
  logicalName: string;
  /** Colonnes attendues : champ interne → en-tête CSV attendu. */
  columns: Record<string, string>;
}

export interface StepDef {
  identifier: string;
  name: string;
  description: string;
  category: StepCategory;
  dependencies: string[];
  /** Fichiers logiques consommés par ce step (vide si pas de fichier). */
  requiredFiles: LogicalFileSpec[];
}

export const STEP_CATALOG: StepDef[] = [
  // Ordre = ImportEngine::STEP_ORDER backend (le tri des étapes sélectionnées
  // suit l'ordre du catalogue). L'école cible étant déjà déployée, chaque
  // étape se suffit de son fichier : les références vers l'existant (niveaux,
  // classes, responsables…) sont résolues contre le contenu de l'école.
  {
    identifier: 'import_levels',
    name: 'Importer les niveaux',
    description: 'Crée les niveaux scolaires (code, MEF, cycle) depuis le fichier niveaux.',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'niveaux',
        columns: {
          name: 'niveau',
          order: 'ordre',
          code: 'code_niveau',
          mefCode: 'codemef',
          cycleCode: 'code_cycle',
        },
      },
    ],
  },
  {
    identifier: 'import_teachers',
    name: 'Importer les enseignants',
    description:
      'Crée les enseignants (inactifs, aucune invitation envoyée). Colonne optionnelle : etablissement (nom ou RNE, sinon établissement par défaut).',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'enseignants',
        columns: {
          firstName: 'prenom',
          lastName: 'nom',
          email: 'email',
          phone: 'telephone',
          establishment: 'etablissement',
        },
      },
    ],
  },
  {
    identifier: 'import_directors',
    name: 'Importer l’équipe de direction',
    description:
      'Crée l’équipe de direction (inactifs, aucune invitation envoyée). Colonne optionnelle : etablissement (nom ou RNE, sinon établissement par défaut).',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'direction',
        columns: {
          firstName: 'prenom',
          lastName: 'nom',
          email: 'email',
          role: 'fonction',
          phone: 'telephone',
          establishment: 'etablissement',
        },
      },
    ],
  },
  {
    identifier: 'import_parents',
    name: 'Importer les parents et représentants légaux',
    description:
      'Crée les responsables légaux (inactifs). Le lien avec les élèves est porté par le fichier des élèves (colonnes email_responsable_*).',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'parents',
        columns: {
          firstName: 'prenom',
          lastName: 'nom',
          email: 'email',
          phone: 'telephone',
          relationType: 'type_relation',
          address: 'adresse',
          // Colonne reprise par la passe ADM post-import.
          profession: 'profession',
        },
      },
    ],
  },
  {
    identifier: 'import_subjects',
    name: 'Importer les matières',
    description:
      'Crée les matières avec coefficient par niveau. Colonnes optionnelles : regroupement (groupe de matières) et enseignants (e-mails séparés par « | », rattachés à la matière).',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'matieres',
        columns: {
          name: 'nom',
          code: 'code_matiere',
          level: 'niveau',
          coefficient: 'coefficient',
          group: 'regroupement',
          teachers: 'enseignants',
        },
      },
    ],
  },
  {
    identifier: 'import_classes',
    name: 'Importer les classes',
    description:
      'Crée les classes. Multi-niveaux : « | » dans code_niveau (ex : CP|CE1). Colonnes optionnelles : professeur_principal (e-mail — sinon un enseignant provisoire est posé) et etablissement (nom ou RNE, sinon établissement par défaut).',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'classes',
        columns: {
          name: 'nom',
          levelCode: 'code_niveau',
          capacity: 'capacite',
          classTeacher: 'professeur_principal',
          establishment: 'etablissement',
        },
      },
    ],
  },
  {
    identifier: 'import_students',
    name: 'Importer les élèves',
    description:
      'Crée les élèves (inactifs) et les rattache à leur classe, à leur niveau et à leur établissement. Le lieu de naissance est repris en texte, et la commune + le pays sont déduits quand la nomenclature les reconnaît sans ambiguïté. L’établissement vient de la classe ; pour un élève sans classe, colonne optionnelle etablissement (nom ou RNE, sinon établissement par défaut).',
    category: 'import',
    dependencies: [],
    requiredFiles: [
      {
        logicalName: 'eleves',
        columns: {
          firstName: 'prenom',
          lastName: 'nom',
          email: 'email',
          birthDate: 'date_naissance',
          gender: 'sexe',
          classroom: 'classe',
          level: 'niveau',
          establishment: 'etablissement',
          phone: 'telephone',
          address: 'adresse',
          // Colonnes reprises par la passe ADM post-import.
          birthPlace: 'lieu_de_naissance',
          ine: 'ine',
          nationality: 'nationalite',
          entryDate: 'date_entree',
          exitDate: 'date_sortie',
          withdrawalDate: 'date_desistement',
          guardianEmail1: 'email_responsable_1',
          guardianEmail2: 'email_responsable_2',
          guardianEmail3: 'email_responsable_3',
          guardianEmail4: 'email_responsable_4',
        },
      },
    ],
  },
  {
    identifier: 'link_guardians',
    name: 'Rattacher les responsables aux élèves',
    description:
      'Crée les liens responsable→élève depuis les colonnes email_responsable_* du fichier élèves. Le libellé de chaque lien vient de la colonne type_relation_* en regard (un même parent peut être « père » d’un enfant et « beau-père » d’un autre). L’élève est retrouvé par e-mail, ou par prénom + nom + date de naissance s’il n’en a pas.',
    category: 'import',
    // Les deux bouts du lien doivent exister avant de le créer.
    dependencies: ['import_parents', 'import_students'],
    requiredFiles: [
      {
        logicalName: 'eleves',
        columns: {
          firstName: 'prenom',
          lastName: 'nom',
          email: 'email',
          birthDate: 'date_naissance',
          guardianEmail1: 'email_responsable_1',
          guardianEmail2: 'email_responsable_2',
          guardianEmail3: 'email_responsable_3',
          guardianEmail4: 'email_responsable_4',
          guardianRelation1: 'type_relation_1',
          guardianRelation2: 'type_relation_2',
          guardianRelation3: 'type_relation_3',
          guardianRelation4: 'type_relation_4',
        },
      },
    ],
  },
];

export const STEP_BY_ID: Record<string, StepDef> = Object.fromEntries(
  STEP_CATALOG.map((s) => [s.identifier, s]),
);

/**
 * Dimension de réconciliation → fichier logique que le back FABRIQUE quand on
 * choisit « Créer » (miroir de `ReconciliationService::createFileFor`).
 */
export const CREATE_FILE_BY_DIMENSION: Record<string, string> = {
  level: 'niveaux',
  class: 'classes',
  subject: 'matieres',
};

/** Fichier logique → étape d'import qui le consomme. */
export const FILE_TO_STEP: Record<string, string> = Object.fromEntries(
  STEP_CATALOG.flatMap((s) => s.requiredFiles.map((f) => [f.logicalName, s.identifier])).filter(
    ([logicalName]) => logicalName !== 'eleves',
  ),
);
// `eleves` alimente deux étapes (import + rattachement) : on garde l'import.
FILE_TO_STEP.eleves = 'import_students';

/** Libellés FR des dimensions de référence (réconciliation). */
export const DIMENSION_LABELS: Record<string, string> = {
  class: 'Classes',
  level: 'Niveaux',
  cycle: 'Cycles',
  subject: 'Matières',
  student: 'Élèves',
};

/** Libellés FR au singulier (formulations « Créer le niveau… »). */
export const DIMENSION_SINGULAR: Record<string, string> = {
  class: 'la classe',
  level: 'le niveau',
  cycle: 'le cycle',
  subject: 'la matière',
  student: 'l’élève',
};

/** Libellés FR des fichiers logiques (affichage utilisateur). */
export const FILE_LABELS: Record<string, string> = {
  niveaux: 'Niveaux',
  matieres: 'Matières',
  classes: 'Classes',
  enseignants: 'Enseignants',
  direction: 'Direction',
  parents: 'Parents',
  eleves: 'Élèves',
};

/** Libellés FR des compteurs d'état du tenant. */
export const OVERVIEW_LABELS: Record<string, string> = {
  cycles: 'Cycles',
  levels: 'Niveaux',
  classes: 'Classes',
  subjects: 'Matières',
  teachers: 'Enseignants',
  students: 'Élèves',
  parents: 'Parents',
  directors: 'Direction',
};

export const CATEGORY_LABELS: Record<StepCategory, string> = {
  init: 'Initialisation',
  import: 'Import de données',
  assignment: 'Affectations',
  post: 'Post-import',
};

export const CATEGORY_ORDER: StepCategory[] = ['init', 'import', 'assignment', 'post'];

/**
 * Ferme transitivement l'ensemble des dépendances d'un step sélectionné :
 * un step ne peut tourner sans ses prérequis.
 */
export function resolveDependencies(selected: Set<string>): Set<string> {
  const result = new Set(selected);
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of Array.from(result)) {
      const def = STEP_BY_ID[id];
      if (!def) continue;
      for (const dep of def.dependencies) {
        if (!result.has(dep)) {
          result.add(dep);
          changed = true;
        }
      }
    }
  }
  return result;
}

/** Trie une liste de steps selon les dépendances (tri topologique stable). */
export function sortStepsByDependencies(ids: string[]): string[] {
  const set = new Set(ids);
  const sorted: string[] = [];
  const visited = new Set<string>();
  const visit = (id: string) => {
    if (visited.has(id) || !set.has(id)) return;
    visited.add(id);
    const def = STEP_BY_ID[id];
    for (const dep of def?.dependencies ?? []) visit(dep);
    sorted.push(id);
  };
  // ordre du catalogue comme base stable
  for (const def of STEP_CATALOG) if (set.has(def.identifier)) visit(def.identifier);
  return sorted;
}

/** Tous les fichiers logiques requis par un ensemble de steps. */
export function logicalFilesForSteps(ids: string[]): LogicalFileSpec[] {
  const seen = new Map<string, LogicalFileSpec>();
  for (const id of ids) {
    for (const f of STEP_BY_ID[id]?.requiredFiles ?? []) {
      if (!seen.has(f.logicalName)) seen.set(f.logicalName, f);
    }
  }
  return Array.from(seen.values());
}

/** Catalogue complet des fichiers logiques possibles (tous steps confondus). */
export const ALL_LOGICAL_FILES: LogicalFileSpec[] = logicalFilesForSteps(
  STEP_CATALOG.map((s) => s.identifier),
);

/**
 * Un step est sélectionnable si TOUS les fichiers de sa fermeture transitive de
 * dépendances sont présents (cohérence d'un déploiement « école neuve » : un
 * import a besoin des fichiers de ses prérequis).
 */
export function closureFilesSatisfied(stepId: string, present: Set<string>): boolean {
  for (const id of resolveDependencies(new Set([stepId]))) {
    const def = STEP_BY_ID[id];
    if (def && def.requiredFiles.some((f) => !present.has(f.logicalName))) {
      return false;
    }
  }
  return true;
}

/**
 * Fichiers manquants empêchant un import cohérent pour la sélection courante :
 * parcourt la fermeture des dépendances et liste les fichiers absents avec le
 * step qui les requiert.
 */
export function missingCoherenceFiles(
  selected: Set<string>,
  present: Set<string>,
): Array<{ stepId: string; spec: LogicalFileSpec }> {
  const out: Array<{ stepId: string; spec: LogicalFileSpec }> = [];
  for (const id of resolveDependencies(selected)) {
    const def = STEP_BY_ID[id];
    if (!def) continue;
    for (const spec of def.requiredFiles) {
      if (!present.has(spec.logicalName)) out.push({ stepId: id, spec });
    }
  }
  return out;
}

// ---------- Options de configuration (libellés FR) ----------

/**
 * Type de déploiement envoyé à la V2. L'assistant ADM ne fait que de l'import
 * par fichiers (pas de migration période→période), donc le type est fixe.
 */
export const IMPORT_DEPLOYMENT_TYPE: DeploymentType = 'new_school';

export const CONFLICT_POLICY_OPTIONS: Array<{ value: ConflictPolicyValue; label: string }> = [
  { value: 'skip', label: 'Ignorer (skip)' },
  { value: 'update_all', label: 'Tout mettre à jour' },
  { value: 'update_empty_only', label: 'Compléter les champs vides uniquement' },
  { value: 'archive_create_new', label: 'Archiver et recréer' },
  { value: 'fail', label: 'Échouer' },
];

export const CONFLICT_ENTITIES: Array<{
  key: Exclude<keyof import('./types').ConflictPolicyConfig, 'treatBatchDuplicatesAsSkip'>;
  label: string;
}> = [
  { key: 'studentsPolicy', label: 'Élèves' },
  { key: 'teachersPolicy', label: 'Enseignants' },
  { key: 'parentsPolicy', label: 'Parents' },
  { key: 'directorsPolicy', label: 'Direction' },
  { key: 'subjectsPolicy', label: 'Matières' },
  { key: 'classesPolicy', label: 'Classes' },
];

export const PARENT_TO_STUDENT_OPTIONS: Array<{ value: ParentToStudentStrategy; label: string }> = [
  { value: 'email', label: 'Email de l’élève' },
  { value: 'lastname', label: 'Nom de famille' },
  { value: 'fullname', label: 'Nom complet' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'address', label: 'Adresse' },
  { value: 'none', label: 'Aucune' },
];

export const STUDENT_TO_CLASS_OPTIONS: Array<{ value: StudentToClassStrategy; label: string }> = [
  { value: 'class_name', label: 'Nom de classe' },
  { value: 'level_only', label: 'Niveau uniquement' },
  { value: 'none', label: 'Aucune' },
];

export const TEACHER_TO_SUBJECT_OPTIONS: Array<{ value: TeacherToSubjectStrategy; label: string }> = [
  { value: 'specialty_name', label: 'Nom de spécialité' },
  { value: 'none', label: 'Aucune' },
];

export const EMAIL_MODE_OPTIONS: Array<{ value: EmailMode; label: string }> = [
  { value: 'lowercase', label: 'Minuscules' },
  { value: 'none', label: 'Aucune' },
];

export const NAME_MODE_OPTIONS: Array<{ value: NameMode; label: string }> = [
  { value: 'title_case', label: 'Capitales initiales' },
  { value: 'upper_case', label: 'MAJUSCULES' },
  { value: 'lower_case', label: 'minuscules' },
  { value: 'none', label: 'Aucune' },
];

export const PHONE_MODE_OPTIONS: Array<{ value: PhoneMode; label: string }> = [
  { value: 'e164', label: 'Format international (E.164)' },
  { value: 'digits_only', label: 'Chiffres uniquement' },
  { value: 'fr_local', label: 'Local FR (0X…)' },
  { value: 'none', label: 'Aucune' },
];

export const CSV_DELIMITER_OPTIONS: Array<{ value: CsvDelimiter; label: string }> = [
  { value: ';', label: 'Point-virgule ( ; )' },
  { value: ',', label: 'Virgule ( , )' },
  { value: '\\t', label: 'Tabulation' },
  { value: '|', label: 'Pipe ( | )' },
];

export const ENCODING_OPTIONS: Array<{ value: Encoding; label: string }> = [
  { value: 'UTF-8', label: 'UTF-8' },
  { value: 'ISO-8859-1', label: 'ISO-8859-1 (Latin-1)' },
  { value: 'Windows-1252', label: 'Windows-1252' },
];

export const ALLOWED_POST_IMPORT_COMMANDS: string[] = [
  'app:school-administration:sync-settings',
  'app:school-administration:sync-role-permissions',
  'app:init-attendance-statuses',
  'app:init-evaluation-types',
  'app:init-relation-types',
  'app:create-default-mail-template',
  'app:dynamic-form:init-data',
  'app:activity:sync-notification-configuration',
  'communication:messaging:bootstrap-channels',
  'communication:messaging:bootstrap-auto-lists',
  'app:time-management:create-default-agenda',
  'billing:init-data',
];
